import type { AttributeReviewValueFixInput } from "@/graphql/generated";

export type PoolValue = {
  store_value: string;
  store_label: string;
  pool_value: string;
  match: string;
  ignored?: boolean;
  ignored_reason?: string | null;
  dropped_words?: string[];
};

export type PoolChoice = { key: string; label: string };

export const fixLabel = (fix: {
  poolValue?: string | null;
  newValue?: PoolChoice | null;
}) => fix.poolValue ?? fix.newValue?.label;

const NOT_CHECKED = "not checked — model call failed";
const NOT_IN_LIST = "not in this closed list — cannot be saved";

const unmatched = (v: PoolValue) =>
  v.match === "not in list" || v.match === "not checked";

const isIgnored = (v: PoolValue, fix?: AttributeReviewValueFixInput) =>
  fix ? Boolean(fix.ignored) : Boolean(v.ignored);

const ourName = (v: PoolValue, fix?: AttributeReviewValueFixInput) =>
  isIgnored(v, fix)
    ? ""
    : (fixLabel(fix ?? {}) ??
      (v.match === "new" ? newValueName(v) : v.pool_value));

// An unmatched store word is not in the pool yet; offering it would save the store's spelling.
export const poolChoices = (
  pool: PoolChoice[],
  values: PoolValue[],
  fixes: Record<string, AttributeReviewValueFixInput>,
) => {
  const spelled = new Set(
    values
      .filter(
        (v) =>
          unmatched(v) ||
          (v.match === "new" &&
            ourName(v, fixes[v.store_value]) !== v.store_label),
      )
      .map((v) => v.store_label),
  );
  return pool.filter((p) => !spelled.has(p.label));
};

export const fixForText = (
  v: PoolValue,
  text: string,
  choices: PoolChoice[],
): AttributeReviewValueFixInput => {
  const back = v.ignored ? { ignored: false } : {};
  const picked = choices.find((c) => c.label === text);
  return picked
    ? { storeValue: v.store_value, poolValue: picked.label, ...back }
    : {
        storeValue: v.store_value,
        newValue: { key: valueKey(text), label: text },
        ...back,
      };
};

export function valueRow(
  v: PoolValue,
  fix: AttributeReviewValueFixInput | undefined,
  choices: PoolChoice[],
) {
  const ignored = isIgnored(v, fix);
  const name = ourName(v, fix);
  const key = valueKey(name);
  const mapsTo =
    fix?.poolValue ??
    (fix?.newValue || v.match === "new" || unmatched(v) ? null : v.pool_value);
  const taken =
    !mapsTo &&
    choices.find((c) => c.key === key && c.key !== valueKey(v.pool_value));
  const closed = v.match === "not in list";
  return {
    name,
    key: ignored || mapsTo || (unmatched(v) && !fix) ? "" : key,
    ignored,
    editable: !ignored && !closed,
    chip: ignored
      ? "ignored"
      : mapsTo
        ? `maps to ${mapsTo}`
        : unmatched(v) && !fix
          ? ""
          : "new",
    attention: ignored
      ? null
      : closed
        ? NOT_IN_LIST
        : !key
          ? "give it a name"
          : taken
            ? `already in our list as “${taken.label}” — clear the box and pick it from the list`
            : v.match === "not checked" && !fix
              ? NOT_CHECKED
              : null,
  };
}

export const canPickAgain = (value: PoolValue) =>
  Boolean(value.ignored) &&
  (!value.ignored_reason || value.ignored_reason === "not checked");

// Mirrors the engine's value_key for display; the engine makes the saved key.
export const valueKey = (label: string) =>
  label
    .normalize("NFKD")
    .replace(/\p{Mn}/gu, "")
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}_\s]/gu, " ")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/ /g, "_");

const singular = (word: string) =>
  word.length < 4 || /(ss|us|is|['’]s)$/i.test(word)
    ? word
    : /ies$/i.test(word)
      ? `${word.slice(0, -3)}y`
      : /(ches|shes|xes|zes|sses)$/i.test(word)
        ? word.slice(0, -2)
        : word.replace(/s$/i, "");

const cleanedName = (storeLabel: string) => {
  const name =
    storeLabel
      .replace(/\([^)]*\)|\[[^\]]*\]/g, " ")
      .split(/\s+[/|]\s*|\s*[/|]\s+/)
      .map((part) => part.trim())
      .filter(Boolean)
      .pop() ?? "";
  const words = name.split(/\s+/).filter(Boolean);
  return words.length
    ? [...words.slice(0, -1), singular(words[words.length - 1])].join(" ")
    : storeLabel.trim();
};

export const newValueName = (value: PoolValue) =>
  value.pool_value !== value.store_label
    ? value.pool_value
    : cleanedName(value.store_label);
